import React, { useState } from 'react';
import { Crown, Trophy, Check, Circle } from 'lucide-react';

// === Sample data based on actual tournament ===
const ROUNDS = [
  {
    name: 'שמינית גמר',
    points: 50,
    matches: [
      { p1: 'אלדד שמואלי', p1Seed: 1, p1Scores: [6, 6], p2: 'אריאל פטסונק', p2Scores: [3, 3], winner: 1 },
      { p1: 'חננאל ביטון', p1Scores: [1, 0], p2: 'Omri singolda', p2Scores: [6, 6], winner: 2 },
      { p1: 'אלכס שר', p1Scores: [2, 2], p2: 'ניר כהן אשכול', p2Seed: 2, p2Scores: [6, 6], winner: 2 },
      { p1: 'אסף ריבר', p1Scores: [4, 6, 3], p2: 'יניב בר-לב', p2Scores: [5, 3, 6], winner: 2 },
    ]
  },
  {
    name: 'רבע גמר',
    points: 80,
    matches: [
      { p1: 'ניר פייגנבוים', p1Scores: [6, 6], p2: 'ליאם אוסטרוביץ', p2Scores: [1, 2], winner: 1 },
      { p1: 'ניר כהן אשכול', p1Seed: 2, p1Scores: [7, 6], p2: 'אבי שושן', p2Scores: [5, 4], winner: 1 },
    ]
  },
  {
    name: 'חצי גמר',
    points: 120,
    matches: [
      { p1: 'ניר פייגנבוים', p1Scores: [5, 4, 6], p2: 'ניר כהן אשכול', p2Seed: 2, p2Scores: [7, 6, 1], winner: 1 },
      { p1: 'אלדד שמואלי', p1Seed: 1, p1Scores: [7, 2, 6], p2: 'ירין ביטון', p2Scores: [6, 6, 3], winner: 1 },
    ]
  },
  {
    name: 'גמר',
    points: 350,
    matches: [
      { p1: 'ניר כהן אשכול', p1Seed: 2, p1Scores: [3, 6], p2: 'אלדד שמואלי', p2Seed: 1, p2Scores: [6, 7], winner: 2 },
    ]
  },
];

// ============================================================
// OPTION 1 — ATP Pro Dark
// ============================================================
const ATPDarkBracket = () => (
  <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 rounded-2xl" dir="rtl">
    <div className="text-center mb-8">
      <div className="inline-block px-6 py-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-full">
        <span className="text-amber-400 text-sm tracking-widest font-bold">PROFESSIONAL TOURNAMENT</span>
      </div>
      <h2 className="text-4xl font-black text-white mt-3">ברקט נוקאאוט</h2>
    </div>
    <div className="flex gap-8 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
      {ROUNDS.map((round, rIdx) => (
        <div key={rIdx} className="flex-shrink-0 min-w-[280px]">
          <div className="mb-6 text-center">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-amber-600 to-yellow-600 rounded-lg shadow-lg shadow-amber-500/20">
              <p className="text-white font-bold text-lg">{round.name}</p>
              <p className="text-amber-100 text-xs">{round.points} נקודות</p>
            </div>
          </div>
          <div className="flex flex-col justify-around h-full gap-4" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
            {round.matches.map((m, mIdx) => (
              <div key={mIdx} className={`relative bg-slate-800/60 backdrop-blur rounded-xl overflow-hidden border ${
                rIdx === ROUNDS.length - 1 ? 'border-amber-500 shadow-2xl shadow-amber-500/30' : 'border-slate-700/50'
              }`}>
                {rIdx === ROUNDS.length - 1 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Crown className="w-8 h-8 text-amber-400 drop-shadow-lg" fill="currentColor" />
                  </div>
                )}
                {/* Player 1 */}
                <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 1 ? 'bg-amber-500/10 border-r-4 border-amber-500' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p1Seed && <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">#{m.p1Seed}</span>}
                    <span className={`${m.winner === 1 ? 'text-amber-300 font-bold' : 'text-slate-300'}`}>{m.p1}</span>
                    {m.winner === 1 && <Check className="w-4 h-4 text-amber-400" />}
                  </div>
                  <div className="flex gap-1">
                    {m.p1Scores.map((s, i) => (
                      <span key={i} className={`w-7 h-7 flex items-center justify-center rounded font-mono font-bold text-sm ${
                        m.winner === 1 ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-400'
                      }`}>{s}</span>
                    ))}
                  </div>
                </div>
                <div className="h-px bg-slate-700/50" />
                {/* Player 2 */}
                <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 2 ? 'bg-amber-500/10 border-r-4 border-amber-500' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p2Seed && <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">#{m.p2Seed}</span>}
                    <span className={`${m.winner === 2 ? 'text-amber-300 font-bold' : 'text-slate-300'}`}>{m.p2}</span>
                    {m.winner === 2 && <Check className="w-4 h-4 text-amber-400" />}
                  </div>
                  <div className="flex gap-1">
                    {m.p2Scores.map((s, i) => (
                      <span key={i} className={`w-7 h-7 flex items-center justify-center rounded font-mono font-bold text-sm ${
                        m.winner === 2 ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-400'
                      }`}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 2 — Tennis Court Green
// ============================================================
const TennisGreenBracket = () => (
  <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 p-8 rounded-2xl border border-green-200" dir="rtl">
    <div className="text-center mb-8">
      <h2 className="text-4xl font-black text-emerald-900">🎾 ברקט נוקאאוט</h2>
      <p className="text-emerald-700 mt-1">Tennis Pro Israel — Tournament Bracket</p>
    </div>
    <div className="flex gap-6 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
      {ROUNDS.map((round, rIdx) => (
        <div key={rIdx} className="flex-shrink-0 min-w-[270px]">
          <div className="mb-5 text-center">
            <div className="inline-block px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg shadow-md">
              <p className="text-white font-bold">{round.name}</p>
              <p className="text-emerald-50 text-xs">⭐ {round.points} נקודות</p>
            </div>
          </div>
          <div className="flex flex-col justify-around gap-3" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
            {round.matches.map((m, mIdx) => (
              <div key={mIdx} className={`bg-white rounded-xl overflow-hidden border-2 transition-all hover:shadow-lg ${
                rIdx === ROUNDS.length - 1 ? 'border-emerald-500 shadow-emerald-200 shadow-xl' : 'border-emerald-200'
              }`}>
                {rIdx === ROUNDS.length - 1 && (
                  <div className="bg-gradient-to-r from-emerald-500 to-green-600 py-1 flex items-center justify-center gap-1">
                    <Trophy className="w-4 h-4 text-white" />
                    <span className="text-white text-xs font-bold">משחק הגמר</span>
                  </div>
                )}
                <div className={`flex items-center justify-between px-3 py-2.5 ${m.winner === 1 ? 'bg-emerald-50' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p1Seed && <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">#{m.p1Seed}</span>}
                    <span className={`text-sm ${m.winner === 1 ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>{m.p1}</span>
                    {m.winner === 1 && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
                  </div>
                  <div className="flex gap-1">
                    {m.p1Scores.map((s, i) => (
                      <span key={i} className={`w-7 h-7 flex items-center justify-center rounded font-mono font-bold text-sm ${
                        m.winner === 1 ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>{s}</span>
                    ))}
                  </div>
                </div>
                <div className="h-px bg-emerald-100" />
                <div className={`flex items-center justify-between px-3 py-2.5 ${m.winner === 2 ? 'bg-emerald-50' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p2Seed && <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">#{m.p2Seed}</span>}
                    <span className={`text-sm ${m.winner === 2 ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>{m.p2}</span>
                    {m.winner === 2 && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
                  </div>
                  <div className="flex gap-1">
                    {m.p2Scores.map((s, i) => (
                      <span key={i} className={`w-7 h-7 flex items-center justify-center rounded font-mono font-bold text-sm ${
                        m.winner === 2 ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 3 — Linear Minimal
// ============================================================
const MinimalBracket = () => (
  <div className="bg-white p-10 rounded-2xl" dir="rtl" style={{
    backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)',
    backgroundSize: '24px 24px'
  }}>
    <div className="mb-10 flex items-baseline justify-between">
      <div>
        <p className="text-xs tracking-[0.3em] text-slate-400 uppercase">Tournament</p>
        <h2 className="text-3xl font-light text-slate-900 mt-1">ברקט נוקאאוט</h2>
      </div>
      <div className="text-left">
        <p className="text-xs text-slate-400">8 שחקנים · 7 משחקים</p>
      </div>
    </div>
    <div className="flex gap-12 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
      {ROUNDS.map((round, rIdx) => (
        <div key={rIdx} className="flex-shrink-0 min-w-[260px]">
          <div className="mb-6 pb-2 border-b border-slate-200">
            <p className="text-xs text-slate-400 tracking-wider uppercase">Round {ROUNDS.length - rIdx}</p>
            <p className="text-sm text-slate-700 font-medium">{round.name}</p>
          </div>
          <div className="flex flex-col justify-around gap-5" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
            {round.matches.map((m, mIdx) => (
              <div key={mIdx} className="group">
                <div className={`relative bg-white border ${
                  rIdx === ROUNDS.length - 1 ? 'border-slate-900 shadow-lg' : 'border-slate-200'
                } rounded-md overflow-hidden transition-all hover:shadow-md`}>
                  {/* Winner accent line */}
                  <div className={`flex items-center justify-between px-3 py-2.5 relative ${m.winner === 1 ? 'bg-slate-50' : ''}`}>
                    {m.winner === 1 && <div className="absolute right-0 top-0 bottom-0 w-1 bg-emerald-500" />}
                    <div className="flex items-center gap-2 flex-1">
                      {m.p1Seed && <span className="text-[10px] text-slate-400 font-mono">[{m.p1Seed}]</span>}
                      <span className={`text-sm ${m.winner === 1 ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>{m.p1}</span>
                    </div>
                    <div className="flex gap-2 font-mono text-sm">
                      {m.p1Scores.map((s, i) => (
                        <span key={i} className={`w-6 text-center ${m.winner === 1 ? 'text-slate-900 font-semibold' : 'text-slate-400'}`}>{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="h-px bg-slate-100" />
                  <div className={`flex items-center justify-between px-3 py-2.5 relative ${m.winner === 2 ? 'bg-slate-50' : ''}`}>
                    {m.winner === 2 && <div className="absolute right-0 top-0 bottom-0 w-1 bg-emerald-500" />}
                    <div className="flex items-center gap-2 flex-1">
                      {m.p2Seed && <span className="text-[10px] text-slate-400 font-mono">[{m.p2Seed}]</span>}
                      <span className={`text-sm ${m.winner === 2 ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>{m.p2}</span>
                    </div>
                    <div className="flex gap-2 font-mono text-sm">
                      {m.p2Scores.map((s, i) => (
                        <span key={i} className={`w-6 text-center ${m.winner === 2 ? 'text-slate-900 font-semibold' : 'text-slate-400'}`}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 4 — Hybrid Premium
// ============================================================
const HybridPremiumBracket = () => (
  <div className="relative p-10 rounded-3xl overflow-hidden" dir="rtl" style={{
    background: 'linear-gradient(135deg, #faf5ff 0%, #ffffff 50%, #f0fdf4 100%)',
  }}>
    {/* Decorative blobs */}
    <div className="absolute top-0 right-0 w-96 h-96 bg-purple-300/20 rounded-full blur-3xl" />
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-300/20 rounded-full blur-3xl" />
    
    <div className="relative">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/60 backdrop-blur-md border border-white/40 rounded-full shadow-sm mb-3">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-xs font-bold text-slate-700 tracking-wider">LIVE TOURNAMENT</span>
        </div>
        <h2 className="text-4xl font-black bg-gradient-to-r from-purple-600 via-pink-500 to-emerald-600 bg-clip-text text-transparent">
          🏆 ברקט הגמר
        </h2>
      </div>
      
      <div className="flex gap-8 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
        {ROUNDS.map((round, rIdx) => (
          <div key={rIdx} className="flex-shrink-0 min-w-[290px]">
            <div className="mb-6 text-center">
              <div className="inline-block px-6 py-3 rounded-2xl shadow-lg" style={{
                background: rIdx === ROUNDS.length - 1
                  ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                  : 'linear-gradient(135deg, #a855f7, #ec4899)'
              }}>
                <p className="text-white font-black text-lg">{round.name}</p>
                <p className="text-white/90 text-xs flex items-center justify-center gap-1">
                  ⭐ {round.points} נקודות
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-around gap-4" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
              {round.matches.map((m, mIdx) => (
                <div key={mIdx} className={`relative backdrop-blur-xl rounded-2xl overflow-hidden transition-all hover:scale-[1.02] ${
                  rIdx === ROUNDS.length - 1
                    ? 'bg-gradient-to-br from-amber-50/80 to-orange-50/80 border-2 border-amber-400 shadow-2xl shadow-amber-300/40'
                    : 'bg-white/70 border border-white/60 shadow-md hover:shadow-xl'
                }`}>
                  {rIdx === ROUNDS.length - 1 && (
                    <>
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                        <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-full p-2 shadow-lg">
                          <Crown className="w-6 h-6 text-white" fill="currentColor" />
                        </div>
                      </div>
                      <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
                        background: 'linear-gradient(135deg, rgba(251,191,36,0.1), transparent 50%)'
                      }} />
                    </>
                  )}
                  
                  {/* Player 1 */}
                  <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 1 ? 'bg-gradient-to-r from-emerald-50/90 to-transparent' : ''}`}>
                    <div className="flex items-center gap-3 flex-1">
                      {/* Avatar circle */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                        m.winner === 1
                          ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-300/50'
                          : 'bg-slate-200 text-slate-500'
                      }`}>
                        {m.p1.charAt(0)}
                      </div>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                          {m.p1Seed && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-bold">#{m.p1Seed}</span>
                          )}
                          <span className={`text-sm ${m.winner === 1 ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>{m.p1}</span>
                          {m.winner === 1 && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {m.p1Scores.map((s, i) => (
                        <span key={i} className={`w-7 h-7 flex items-center justify-center rounded-lg font-mono font-bold text-sm transition-all ${
                          m.winner === 1
                            ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-400'
                        }`}>{s}</span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Divider with VS */}
                  <div className="relative h-px bg-slate-200">
                    <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-1/2 px-2 bg-white/90 rounded text-[10px] text-slate-400 font-bold">VS</div>
                  </div>
                  
                  {/* Player 2 */}
                  <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 2 ? 'bg-gradient-to-r from-emerald-50/90 to-transparent' : ''}`}>
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                        m.winner === 2
                          ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-300/50'
                          : 'bg-slate-200 text-slate-500'
                      }`}>
                        {m.p2.charAt(0)}
                      </div>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                          {m.p2Seed && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-bold">#{m.p2Seed}</span>
                          )}
                          <span className={`text-sm ${m.winner === 2 ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>{m.p2}</span>
                          {m.winner === 2 && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {m.p2Scores.map((s, i) => (
                        <span key={i} className={`w-7 h-7 flex items-center justify-center rounded-lg font-mono font-bold text-sm transition-all ${
                          m.winner === 2
                            ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-400'
                        }`}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// OPTION 5 — Wimbledon Royal (Classic Elegance)
// ============================================================
const WimbledonBracket = () => (
  <div className="p-10 rounded-2xl" dir="rtl" style={{
    background: 'linear-gradient(135deg, #014421 0%, #0a5c2e 50%, #014421 100%)',
  }}>
    <div className="text-center mb-10">
      <div className="inline-block">
        <p className="text-amber-300 text-xs tracking-[0.4em] mb-2" style={{ fontFamily: 'Georgia, serif' }}>EST. 2024</p>
        <h2 className="text-5xl text-white font-light tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
          The Championships
        </h2>
        <div className="flex items-center gap-3 justify-center mt-3">
          <div className="h-px w-12 bg-amber-300/50" />
          <span className="text-amber-200 text-sm tracking-widest">תחרות נוקאאוט</span>
          <div className="h-px w-12 bg-amber-300/50" />
        </div>
      </div>
    </div>
    <div className="flex gap-8 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
      {ROUNDS.map((round, rIdx) => (
        <div key={rIdx} className="flex-shrink-0 min-w-[290px]">
          <div className="mb-6 text-center border-b border-amber-300/30 pb-3">
            <p className="text-amber-200 tracking-[0.2em] text-xs">ROUND {ROUNDS.length - rIdx}</p>
            <p className="text-white text-xl font-light mt-1" style={{ fontFamily: 'Georgia, serif' }}>{round.name}</p>
            <p className="text-amber-300/70 text-xs mt-1">{round.points} pts</p>
          </div>
          <div className="flex flex-col justify-around gap-4" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
            {round.matches.map((m, mIdx) => (
              <div key={mIdx} className={`bg-white/5 backdrop-blur rounded-lg overflow-hidden border ${
                rIdx === ROUNDS.length - 1 ? 'border-amber-300 shadow-2xl shadow-amber-500/20' : 'border-white/10'
              }`}>
                <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 1 ? 'bg-amber-300/10' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p1Seed && <span className="text-amber-300 text-xs font-serif italic">[{m.p1Seed}]</span>}
                    <span className={`${m.winner === 1 ? 'text-amber-200 font-semibold' : 'text-white/70'}`} style={{ fontFamily: 'Georgia, serif' }}>{m.p1}</span>
                  </div>
                  <div className="flex gap-2 font-serif">
                    {m.p1Scores.map((s, i) => (
                      <span key={i} className={`w-6 text-center ${m.winner === 1 ? 'text-amber-300 font-bold text-lg' : 'text-white/50'}`}>{s}</span>
                    ))}
                  </div>
                </div>
                <div className="h-px bg-amber-300/20 mx-4" />
                <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 2 ? 'bg-amber-300/10' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p2Seed && <span className="text-amber-300 text-xs font-serif italic">[{m.p2Seed}]</span>}
                    <span className={`${m.winner === 2 ? 'text-amber-200 font-semibold' : 'text-white/70'}`} style={{ fontFamily: 'Georgia, serif' }}>{m.p2}</span>
                  </div>
                  <div className="flex gap-2 font-serif">
                    {m.p2Scores.map((s, i) => (
                      <span key={i} className={`w-6 text-center ${m.winner === 2 ? 'text-amber-300 font-bold text-lg' : 'text-white/50'}`}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 6 — Neon Esports
// ============================================================
const NeonEsportsBracket = () => (
  <div className="p-10 rounded-2xl relative overflow-hidden" dir="rtl" style={{
    background: 'radial-gradient(ellipse at top, #1e1b4b 0%, #0c0a1e 70%, #000 100%)',
  }}>
    <div className="absolute inset-0 opacity-30" style={{
      backgroundImage: 'linear-gradient(rgba(34,211,238,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.1) 1px, transparent 1px)',
      backgroundSize: '50px 50px'
    }} />
    <div className="relative">
      <div className="text-center mb-10">
        <div className="inline-block px-6 py-2 bg-cyan-500/10 border border-cyan-400 rounded-full mb-3" style={{ boxShadow: '0 0 30px rgba(34,211,238,0.4)' }}>
          <span className="text-cyan-300 font-mono text-xs tracking-widest">⚡ NEXT-GEN BRACKET ⚡</span>
        </div>
        <h2 className="text-5xl font-black text-white" style={{ textShadow: '0 0 20px rgba(34,211,238,0.8), 0 0 40px rgba(34,211,238,0.4)' }}>
          TOURNAMENT
        </h2>
      </div>
      <div className="flex gap-8 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
        {ROUNDS.map((round, rIdx) => (
          <div key={rIdx} className="flex-shrink-0 min-w-[290px]">
            <div className="mb-6 text-center">
              <div className="inline-block px-5 py-2 bg-black/40 border border-cyan-400 rounded-lg" style={{ boxShadow: '0 0 20px rgba(34,211,238,0.4)' }}>
                <p className="text-cyan-300 font-bold font-mono tracking-wider">{round.name}</p>
                <p className="text-cyan-500/70 text-xs">{round.points} XP</p>
              </div>
            </div>
            <div className="flex flex-col justify-around gap-4" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
              {round.matches.map((m, mIdx) => {
                const isWinnerCard = rIdx === ROUNDS.length - 1;
                return (
                <div key={mIdx} className={`bg-black/50 backdrop-blur rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                  isWinnerCard ? 'border-pink-500' : 'border-cyan-400/50'
                }`} style={{
                  boxShadow: isWinnerCard
                    ? '0 0 30px rgba(236,72,153,0.6), inset 0 0 20px rgba(236,72,153,0.1)'
                    : '0 0 15px rgba(34,211,238,0.2)'
                }}>
                  <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 1 ? 'bg-cyan-500/20' : ''}`}>
                    <div className="flex items-center gap-2 flex-1">
                      {m.p1Seed && <span className="text-pink-400 text-xs font-mono font-bold">#{m.p1Seed}</span>}
                      <span className={`font-mono text-sm ${m.winner === 1 ? 'text-cyan-300 font-bold' : 'text-white/60'}`} style={{ textShadow: m.winner === 1 ? '0 0 10px rgba(34,211,238,0.8)' : 'none' }}>{m.p1}</span>
                      {m.winner === 1 && <span className="text-pink-400 text-xs">▶</span>}
                    </div>
                    <div className="flex gap-1">
                      {m.p1Scores.map((s, i) => (
                        <span key={i} className={`w-7 h-7 flex items-center justify-center rounded font-mono font-bold text-sm ${
                          m.winner === 1
                            ? 'bg-cyan-400 text-black' : 'bg-slate-800 text-slate-500 border border-slate-700'
                        }`}>{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="h-px bg-cyan-400/30" />
                  <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 2 ? 'bg-cyan-500/20' : ''}`}>
                    <div className="flex items-center gap-2 flex-1">
                      {m.p2Seed && <span className="text-pink-400 text-xs font-mono font-bold">#{m.p2Seed}</span>}
                      <span className={`font-mono text-sm ${m.winner === 2 ? 'text-cyan-300 font-bold' : 'text-white/60'}`} style={{ textShadow: m.winner === 2 ? '0 0 10px rgba(34,211,238,0.8)' : 'none' }}>{m.p2}</span>
                      {m.winner === 2 && <span className="text-pink-400 text-xs">▶</span>}
                    </div>
                    <div className="flex gap-1">
                      {m.p2Scores.map((s, i) => (
                        <span key={i} className={`w-7 h-7 flex items-center justify-center rounded font-mono font-bold text-sm ${
                          m.winner === 2
                            ? 'bg-cyan-400 text-black' : 'bg-slate-800 text-slate-500 border border-slate-700'
                        }`}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// OPTION 7 — Newspaper Classic
// ============================================================
const NewspaperBracket = () => (
  <div className="p-10 rounded-2xl bg-stone-50 border-4 border-stone-900" dir="rtl" style={{
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 30px, rgba(0,0,0,0.02) 30px, rgba(0,0,0,0.02) 31px)'
  }}>
    <div className="text-center mb-8 pb-6 border-b-4 border-double border-stone-900">
      <p className="text-stone-500 text-xs tracking-[0.4em]" style={{ fontFamily: 'Georgia, serif' }}>★ ★ ★ TENNIS PRO ISRAEL ★ ★ ★</p>
      <h2 className="text-5xl text-stone-900 font-black tracking-tight mt-2" style={{ fontFamily: 'Georgia, serif' }}>
        THE BRACKET
      </h2>
      <p className="text-stone-600 italic text-sm mt-2" style={{ fontFamily: 'Georgia, serif' }}>"Where legends are made" — Edition #001</p>
    </div>
    <div className="flex gap-6 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
      {ROUNDS.map((round, rIdx) => (
        <div key={rIdx} className="flex-shrink-0 min-w-[270px]">
          <div className="mb-5 text-center">
            <h3 className="text-stone-900 font-black text-lg tracking-wider" style={{ fontFamily: 'Georgia, serif' }}>
              {round.name}
            </h3>
            <div className="flex items-center gap-2 justify-center mt-1">
              <div className="h-px w-8 bg-stone-900" />
              <p className="text-xs text-stone-500 font-mono">{round.points} PTS</p>
              <div className="h-px w-8 bg-stone-900" />
            </div>
          </div>
          <div className="flex flex-col justify-around gap-4" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
            {round.matches.map((m, mIdx) => (
              <div key={mIdx} className={`bg-stone-50 border-2 ${
                rIdx === ROUNDS.length - 1 ? 'border-stone-900 border-double border-4' : 'border-stone-900'
              } shadow-[4px_4px_0_0_rgba(0,0,0,0.9)]`}>
                <div className={`flex items-center justify-between px-3 py-2.5 ${m.winner === 1 ? 'bg-stone-900 text-stone-50' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p1Seed && <span className="text-xs font-mono opacity-70">({m.p1Seed})</span>}
                    <span className={`font-semibold ${m.winner === 1 ? 'text-stone-50' : 'text-stone-700'}`} style={{ fontFamily: 'Georgia, serif' }}>{m.p1}</span>
                  </div>
                  <div className="flex gap-2 font-mono">
                    {m.p1Scores.map((s, i) => (
                      <span key={i} className={`w-6 text-center font-bold ${m.winner === 1 ? 'text-stone-50' : 'text-stone-500'}`}>{s}</span>
                    ))}
                  </div>
                </div>
                <div className="h-px bg-stone-900" />
                <div className={`flex items-center justify-between px-3 py-2.5 ${m.winner === 2 ? 'bg-stone-900 text-stone-50' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p2Seed && <span className="text-xs font-mono opacity-70">({m.p2Seed})</span>}
                    <span className={`font-semibold ${m.winner === 2 ? 'text-stone-50' : 'text-stone-700'}`} style={{ fontFamily: 'Georgia, serif' }}>{m.p2}</span>
                  </div>
                  <div className="flex gap-2 font-mono">
                    {m.p2Scores.map((s, i) => (
                      <span key={i} className={`w-6 text-center font-bold ${m.winner === 2 ? 'text-stone-50' : 'text-stone-500'}`}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 8 — Sunset Warm
// ============================================================
const SunsetBracket = () => (
  <div className="p-10 rounded-2xl" dir="rtl" style={{
    background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 30%, #fbcfe8 70%, #fce7f3 100%)',
  }}>
    <div className="text-center mb-10">
      <h2 className="text-5xl font-black" style={{
        background: 'linear-gradient(135deg, #ea580c, #ec4899, #a855f7)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        🌅 Beach Tennis Bracket
      </h2>
      <p className="text-orange-700 mt-2 font-medium">Summer Tournament Edition</p>
    </div>
    <div className="flex gap-8 overflow-x-auto pb-4" style={{ direction: 'rtl' }}>
      {ROUNDS.map((round, rIdx) => (
        <div key={rIdx} className="flex-shrink-0 min-w-[290px]">
          <div className="mb-6 text-center">
            <div className="inline-block px-5 py-2.5 rounded-2xl shadow-lg" style={{
              background: rIdx === ROUNDS.length - 1
                ? 'linear-gradient(135deg, #f59e0b, #dc2626)'
                : 'linear-gradient(135deg, #fb923c, #ec4899)'
            }}>
              <p className="text-white font-black">{round.name}</p>
              <p className="text-white/90 text-xs">☀️ {round.points} נקודות</p>
            </div>
          </div>
          <div className="flex flex-col justify-around gap-4" style={{ minHeight: rIdx === 0 ? '600px' : 'auto' }}>
            {round.matches.map((m, mIdx) => (
              <div key={mIdx} className={`bg-white/80 backdrop-blur rounded-2xl overflow-hidden border-2 transition-all hover:-translate-y-1 hover:shadow-2xl ${
                rIdx === ROUNDS.length - 1 ? 'border-orange-500 shadow-orange-200 shadow-2xl' : 'border-orange-200'
              }`} style={{
                boxShadow: rIdx === ROUNDS.length - 1 ? '0 10px 40px rgba(249,115,22,0.3)' : '0 4px 15px rgba(0,0,0,0.05)'
              }}>
                <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 1 ? 'bg-gradient-to-r from-orange-100 to-transparent' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p1Seed && <span className="text-xs px-1.5 py-0.5 bg-orange-200 text-orange-800 rounded-full font-bold">#{m.p1Seed}</span>}
                    <span className={`text-sm ${m.winner === 1 ? 'text-orange-900 font-bold' : 'text-stone-600'}`}>{m.p1}</span>
                    {m.winner === 1 && <span className="text-orange-500">🔥</span>}
                  </div>
                  <div className="flex gap-1">
                    {m.p1Scores.map((s, i) => (
                      <span key={i} className={`w-7 h-7 flex items-center justify-center rounded-full font-mono font-bold text-sm ${
                        m.winner === 1 ? 'bg-gradient-to-br from-orange-400 to-pink-500 text-white shadow-md' : 'bg-orange-50 text-stone-400'
                      }`}>{s}</span>
                    ))}
                  </div>
                </div>
                <div className="h-px bg-orange-200/50" />
                <div className={`flex items-center justify-between px-4 py-3 ${m.winner === 2 ? 'bg-gradient-to-r from-orange-100 to-transparent' : ''}`}>
                  <div className="flex items-center gap-2 flex-1">
                    {m.p2Seed && <span className="text-xs px-1.5 py-0.5 bg-orange-200 text-orange-800 rounded-full font-bold">#{m.p2Seed}</span>}
                    <span className={`text-sm ${m.winner === 2 ? 'text-orange-900 font-bold' : 'text-stone-600'}`}>{m.p2}</span>
                    {m.winner === 2 && <span className="text-orange-500">🔥</span>}
                  </div>
                  <div className="flex gap-1">
                    {m.p2Scores.map((s, i) => (
                      <span key={i} className={`w-7 h-7 flex items-center justify-center rounded-full font-mono font-bold text-sm ${
                        m.winner === 2 ? 'bg-gradient-to-br from-orange-400 to-pink-500 text-white shadow-md' : 'bg-orange-50 text-stone-400'
                      }`}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// MAIN PAGE
// ============================================================
const OPTIONS = [
  { key: 'hybrid', label: '⭐ Hybrid Premium', component: HybridPremiumBracket, desc: 'גלאס-מורפיזם, גרדיאנטים, אבטרים, וזהות הצבעים של האתר. אווירת ATP מודרני.' },
  { key: 'atp', label: '🌑 ATP Pro Dark', component: ATPDarkBracket, desc: 'כהה, יוקרתי, פסי זהב. דומה ל-Wimbledon ו-US Open.' },
  { key: 'wimbledon', label: '👑 Wimbledon Royal', component: WimbledonBracket, desc: 'ירוק עמוק עם זהב. אלגנטיות קלאסית עם טיפוגרפיה סריפית - הסגנון הבריטי הצרוף.' },
  { key: 'green', label: '🎾 Tennis Court Green', component: TennisGreenBracket, desc: 'שומר על הזהות הירוקה הקיימת אבל הרבה יותר נקי ומקצועי.' },
  { key: 'neon', label: '⚡ Neon Esports', component: NeonEsportsBracket, desc: 'כהה עם זוהר ניאוני ציאן-ורוד. סטייל גיימינג/esports. גריד תחתי, אפקטי זוהר.' },
  { key: 'newspaper', label: '📰 Newspaper Classic', component: NewspaperBracket, desc: 'שחור-לבן בסגנון עיתון ספורט קלאסי. צללים גרפיים, טיפוגרפיה עתיקה.' },
  { key: 'sunset', label: '🌅 Sunset Warm', component: SunsetBracket, desc: 'גרדיאנטים חמים כתום-ורוד-סגול. תחושה של ביץ׳-טניס וקיץ.' },
  { key: 'minimal', label: '⚪ Linear Minimal', component: MinimalBracket, desc: 'מינימליסטי-נקי בסגנון Linear/Vercel/Notion. מודרני 2026.' },
];

const BracketPreviewPage = () => {
  const [selected, setSelected] = useState('hybrid');
  const Selected = OPTIONS.find(o => o.key === selected);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-slate-900">🎨 הדמיית עיצוב ברקט</h1>
              <p className="text-sm text-slate-500">בחר/י את הסגנון שאת/ה הכי אוהב/ת — ואני אבנה אותו באתר.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSelected(opt.key)}
                  data-testid={`design-option-${opt.key}`}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    selected === opt.key
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-105'
                      : 'bg-white border border-slate-200 text-slate-700 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3 px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-900"><strong>{Selected.label}:</strong> {Selected.desc}</p>
          </div>
        </div>
      </div>

      {/* Bracket Display */}
      <div className="max-w-7xl mx-auto p-6">
        <Selected.component />
      </div>

      {/* CTA */}
      <div className="max-w-7xl mx-auto p-6 pb-12">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-black mb-2">בחרת? 🎯</h3>
          <p className="text-purple-100 mb-4">חזור/י לצ׳אט וכתוב/י לי איזה סגנון בחרת ואני אבנה אותו באתר תוך כמה דקות.</p>
          <div className="flex flex-wrap justify-center gap-3 text-sm font-bold">
            <span className="px-4 py-2 bg-white/20 rounded-full">"אני בוחר Hybrid Premium"</span>
            <span className="px-4 py-2 bg-white/20 rounded-full">"רוצה ATP Dark"</span>
            <span className="px-4 py-2 bg-white/20 rounded-full">"Tennis Green"</span>
            <span className="px-4 py-2 bg-white/20 rounded-full">"Linear Minimal"</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BracketPreviewPage;
